/**
 * MY PreSet plug-in entry-point.
 *   App 부팅 시 한 번 import 되어 side-effect 로
 *     - chatflows
 *     - panels
 *     - e-Filing components
 *     - address reference data
 *   를 shared registry 에 등록한다.
 *
 * 등록 위치 — `frontend/src/main.ts`:
 *   import '@/features/startTask/preset_task/my/preset';
 */

// 1) ChatFlows
import './chatflows';

// 2) Panels (PreSetDocGenView 가 동적으로 lookup)
import { registerPanel } from '../../shared/preset/panels/panelRegistry';
import PreSetIncorporationPanel from './components/PreSetIncorporationPanel.vue';
import PreSetChangeAddressPanel from './components/PreSetChangeAddressPanel.vue';
import PreSetChangeBusinessAddressPanel from './components/PreSetChangeBusinessAddressPanel.vue';
import PreSetUpdateBusinessAddressPanel from './components/PreSetUpdateBusinessAddressPanel.vue';
import PreSetAGMPanel from './components/PreSetAGMPanel.vue';
import PreSetChangeOfficerPanel from './components/PreSetChangeOfficerPanel.vue';
import PreSetAnnualReturnPanel from './components/PreSetAnnualReturnPanel.vue';
import PreSetAppointmentOfSecretaryPanel from './components/PreSetAppointmentOfSecretaryPanel.vue';
import PreSetIncorporationPostPanel from './components/PreSetIncorporationPostPanel.vue';
import PreSetAllotmentSharesPanel from './components/PreSetAllotmentSharesPanel.vue';
import PreSetAllotmentOtcPanel from './components/PreSetAllotmentOtcPanel.vue';
import PreSetBonusIssuePanel from './components/PreSetBonusIssuePanel.vue';
import PreSetBoNoticesPanel from './components/PreSetBoNoticesPanel.vue';
import PreSetBoLettersPanel from './components/PreSetBoLettersPanel.vue';
import PreSetDeclarationNonBoPanel from './components/PreSetDeclarationNonBoPanel.vue';
import PreSetExtensionOfTimePanel from './components/PreSetExtensionOfTimePanel.vue';
import PreSetExemptPrivateCompanyPanel from './components/PreSetExemptPrivateCompanyPanel.vue';
import PreSetShareCertificatePanel from './components/PreSetShareCertificatePanel.vue';
import PreSetTransferSharesPanel from './components/PreSetTransferSharesPanel.vue';
import PreSetCddPanel from './components/PreSetCddPanel.vue';
import PreSetEddPanel from './components/PreSetEddPanel.vue';
import PreSetDirectorResignationPanel from './components/PreSetDirectorResignationPanel.vue';
import PreSetSecretaryResignationPanel from './components/PreSetSecretaryResignationPanel.vue';
import PreSetIncorporationDocPanel from './components/PreSetIncorporationDocPanel.vue';
import PreSetIncorporationPrePanel from './components/PreSetIncorporationPrePanel.vue';
import PreSetNameReservationPanel from './components/PreSetNameReservationPanel.vue';

const matchByCategory = (kw: RegExp) => (sel: any) => {
  const a = sel?.parentCategory?.CATEGORY_NAME || '';
  const b = sel?.category?.CATEGORY_NAME || '';
  return kw.test(`${a} ${b}`);
};
const matchByTaskCode = (codes: string[]) => (sel: any) => {
  const cd = String(sel?.task?.TASK_CODE || '').trim();
  return codes.includes(cd);
};

registerPanel({
  // Post-Incorporation Package (M32101). 'Incorporation(Post)' 카테고리 — task code 로 매칭.
  //   priority 10 으로 my-incorporation (default 0) 보다 먼저 매칭 (둘 다 /incorporat/i 정규식에 걸림).
  id: 'my-incorporation-post',
  match: matchByTaskCode(['M32101']),
  component: PreSetIncorporationPostPanel,
  meta: { docType: 'INCORPORATION_POST' },
  priority: 10,
});
registerPanel({
  // Incorporation DOC (M32014 / M32104). Studio live document — Section 14 / Section 201.
  //   priority 10 으로 my-incorporation (category /incorporat/i) 보다 우선.
  //   레거시 AGM Unaudited 가 동일 코드(M32014)인 경우 AGM 카테고리는 제외.
  id: 'my-incorporation-doc',
  match: (sel: any) => {
    const cd = String(sel?.task?.TASK_CODE || '').trim();
    if (cd !== 'M32014' && cd !== 'M32104') return false;
    const cat = `${sel?.parentCategory?.CATEGORY_NAME || ''} ${sel?.category?.CATEGORY_NAME || ''} ${sel?.task?.TASK_NAME || ''}`;
    if (/annual\s*general|\bagm\b/i.test(cat)) return false;
    return true;
  },
  component: PreSetIncorporationDocPanel,
  meta: { docType: 'INCORPORATION_DOC', studio: true },
  priority: 10,
});
registerPanel({
  // Incorporation (Pre) 스튜디오 (M32100) — 6문서(Section 14 + Section 201(±attestation) + CDD 3종) 편집.
  //   기존 다이렉트 생성 패널(my-incorporation, priority 0) 대신 task code 매칭 + priority 10 으로 우선.
  id: 'my-incorporation-pre',
  match: matchByTaskCode(['M32100']),
  component: PreSetIncorporationPrePanel,
  meta: { docType: 'INCORPORATION_PRE', studio: true },
  priority: 10,
});
registerPanel({
  // Name Reservation (M32001) — SSM 이름 예약. **문서가 없는** 유일한 Incorporation 프리셋이라
  //   문서 패널을 태우면 "No templates linked" 로 막힌다. 전용 패널로 task 만 만들고 e-Filing 으로 보낸다.
  //   INCORPORATION 카테고리에 있어 아래 my-incorporation(카테고리 매칭)과 겹치므로 priority 10.
  id: 'my-name-reservation',
  match: matchByTaskCode(['M32001']),
  component: PreSetNameReservationPanel,
  meta: { docType: 'NAME_RESERVATION', studio: true },
  priority: 10,
});
registerPanel({
  id: 'my-incorporation',
  match: matchByCategory(/incorporat/i),
  component: PreSetIncorporationPanel,
  meta: { docType: 'INCORPORATION' },
});
registerPanel({
  // Change Business Address (Section 47(3) / PD 2/2017, M32017). DCR + Form PD2 + Section 47(3) 3종 Studio.
  //   category 'change address' 로도 my-change-address 에 걸리므로 task code + priority 10 으로 우선.
  id: 'my-change-business-address',
  match: matchByTaskCode(['M32017']),
  component: PreSetChangeBusinessAddressPanel,
  meta: { docType: 'CHANGE_BUSINESS_ADDRESS_473', studio: true },
  priority: 10,
});
registerPanel({
  // Update Business Address (Section 47(2) / PD 2/2017, M32018). DCR + Form PD2 + Section 47(2) 3종 Studio.
  //   category 'change address' 로도 걸리므로 task code + priority 10 으로 우선.
  id: 'my-update-business-address',
  match: matchByTaskCode(['M32018']),
  component: PreSetUpdateBusinessAddressPanel,
  meta: { docType: 'UPDATE_BUSINESS_ADDRESS_472', studio: true },
  priority: 10,
});
registerPanel({
  id: 'my-change-address',
  match: matchByCategory(/change\s*address/i),
  component: PreSetChangeAddressPanel,
});
registerPanel({
  id: 'my-agm',
  match: matchByCategory(/annual\s*general\s*meeting|\bagm\b/i),
  component: PreSetAGMPanel,
});
registerPanel({
  id: 'my-change-officer',
  // Change Officer 카테고리 — 현재 task code: M32105 (Appointment of Director)
  match: matchByCategory(/change\s*officer/i),
  component: PreSetChangeOfficerPanel,
  meta: { docType: 'CHANGE_OFFICER' },
});
registerPanel({
  // Same Change Officer 카테고리이지만 M32117 (Appointment of Secretary) 는 secretary 변수/UI 가
  //   AOD 와 크게 달라 별도 panel 사용. priority 10 으로 my-change-officer (default 0) 보다 먼저 매칭.
  id: 'my-appointment-of-secretary',
  match: matchByTaskCode(['M32117']),
  component: PreSetAppointmentOfSecretaryPanel,
  meta: { docType: 'APPOINTMENT_OF_SECRETARY' },
  priority: 10,
});
registerPanel({
  // Resignation of Director (M32106). 문서-뷰 Studio — MVP DirectorResignationDoc + ConversationPanel.
  //   Change Officer 카테고리와 겹치므로 task code + priority 로 my-change-officer 보다 우선.
  id: 'my-director-resignation',
  match: matchByTaskCode(['M32106']),
  component: PreSetDirectorResignationPanel,
  meta: { docType: 'RESIGNATION_OF_DIRECTOR', studio: true },
  priority: 10,
});
registerPanel({
  // Resignation of Secretary (M32107). 문서-뷰 Studio — MVP SecretaryResignationDoc + ConversationPanel.
  //   Change Officer 카테고리와 겹치므로 task code + priority 로 my-change-officer 보다 우선.
  id: 'my-secretary-resignation',
  match: matchByTaskCode(['M32107']),
  component: PreSetSecretaryResignationPanel,
  meta: { docType: 'RESIGNATION_OF_SECRETARY', studio: true },
  priority: 10,
});
registerPanel({
  // Allotment of Shares (Cash) (M32200). 11개 문서 패키지.
  //   카테고리 이름이 'Allotment' 류로 들어올 수 있으나 task code 매칭이 안전.
  id: 'my-allotment-shares',
  match: matchByTaskCode(['M32200']),
  component: PreSetAllotmentSharesPanel,
  meta: { docType: 'ALLOTMENT_SHARES_CASH' },
  priority: 10,
});
registerPanel({
  // Allotment of Shares — Otherwise Than Cash (M32202). 12문서 패키지(4단계) 문서-뷰 Studio.
  //   CDD 미러링 — 오른쪽 중앙 문서 패키지 + 왼쪽 챗봇(person lookup).
  id: 'my-allotment-shares-otc',
  match: matchByTaskCode(['M32202']),
  component: PreSetAllotmentOtcPanel,
  meta: { docType: 'ALLOTMENT_SHARES_OTC', studio: true },
  priority: 10,
});
registerPanel({
  // Allotment of Shares — Bonus Issue (M32203). 8문서 패키지(4단계) 문서-뷰 Studio.
  //   OTC 미러링 — 준비금 자본전입으로 기존 주주에게 무상 배정. 오른쪽 문서 패키지 + 왼쪽 챗봇.
  id: 'my-allotment-shares-bonus',
  match: matchByTaskCode(['M32203']),
  component: PreSetBonusIssuePanel,
  meta: { docType: 'ALLOTMENT_SHARES_BONUS', studio: true },
  priority: 10,
});
registerPanel({
  // Beneficial Owner Notices (Section 60C, M32108). 6문서 패키지(2단계) 문서-뷰 Studio.
  id: 'my-bo-notices-60c',
  match: matchByTaskCode(['M32108']),
  component: PreSetBoNoticesPanel,
  meta: { docType: 'BO_NOTICES_60C', studio: true },
  priority: 10,
});
registerPanel({
  // Beneficial Owner Letters — Appendix A/B/C (M32109). 설문 선택 후 플랫 문서 패키지 Studio.
  id: 'my-bo-letters-appendix',
  match: matchByTaskCode(['M32109']),
  component: PreSetBoLettersPanel,
  meta: { docType: 'BO_LETTERS_APPENDIX', studio: true },
  priority: 10,
});
registerPanel({
  // Declaration of Non-Beneficial Owner (Not a BO, M32110). 단일 선언 레터 Studio.
  id: 'my-declaration-non-bo',
  match: matchByTaskCode(['M32110']),
  component: PreSetDeclarationNonBoPanel,
  meta: { docType: 'DECLARATION_NON_BO', studio: true },
  priority: 10,
});
registerPanel({
  // Extension of Time (Section 259(2), M32015). DCR + Notice 2종 Studio.
  id: 'my-extension-of-time',
  match: matchByTaskCode(['M32015']),
  component: PreSetExtensionOfTimePanel,
  meta: { docType: 'EXTENSION_OF_TIME_259', studio: true },
  priority: 10,
});
registerPanel({
  // Exempt Private Company Certificate (Section 260 & 261, M32016). Certificate + Auditors Statement.
  id: 'my-exempt-private-company',
  match: matchByTaskCode(['M32016']),
  component: PreSetExemptPrivateCompanyPanel,
  meta: { docType: 'EXEMPT_PRIVATE_COMPANY_260', studio: true },
  priority: 10,
});
registerPanel({
  // Share Certificate (M32205). Transfer Share Certificate 단독 Studio.
  id: 'my-share-certificate',
  match: matchByTaskCode(['M32205']),
  component: PreSetShareCertificatePanel,
  meta: { docType: 'SHARE_CERTIFICATE', studio: true },
  priority: 10,
});
registerPanel({
  // Transfer of Shares — with SC(M32201) / without SC(M32204). 문서-뷰 Studio, 동일 패널.
  //   연결된 템플릿에 따라 단계 자동 분기(with=4단계, without=Waiver·Share Certificate 미연결 → 2단계).
  //   카테고리 'Transfer of Shares' 가능하나 task code 매칭이 안전. priority 10.
  id: 'my-transfer-shares',
  match: matchByTaskCode(['M32201', 'M32204']),
  component: PreSetTransferSharesPanel,
  // studio: 문서-뷰 셸(중앙 문서 패키지 + 플로팅 챗봇)로 렌더. (M32202 OTC 미러링)
  meta: { docType: 'TRANSFER_OF_SHARES', studio: true },
  priority: 10,
});
registerPanel({
  // Customer Due Diligence (M32210). 문서-뷰 방식 — 3종 CDD 폼(Individual/Legal Person/Legal Arrangement)
  //   을 한 task 에서 여러 건 작성. 오른쪽 = 문서, 왼쪽 = 챗봇(person lookup).
  id: 'my-cdd',
  match: matchByTaskCode(['M32210']),
  component: PreSetCddPanel,
  // studio: 새 Preset Studio 셸(중앙 문서 + 플로팅 챗봇)로 렌더. CDD 부터 적용, 검증 후 확장.
  meta: { docType: 'CDD', studio: true },
  priority: 10,
});
registerPanel({
  // Enhanced Customer Due Diligence (M32211). 단일 EDD 폼 × 다건(Add EDD).
  //   Entity 필수 — 기존 법인 officer 조회. CDD(M32210) 와 별도.
  id: 'my-edd',
  match: matchByTaskCode(['M32211']),
  component: PreSetEddPanel,
  meta: { docType: 'EDD', studio: true },
  priority: 10,
});
registerPanel({
  id: 'my-annual-return',
  // Annual Return 카테고리 — M32115 (Lodgement of Annual Return DCR).
  //   AGM 의 'Annual General Meeting' 와 헷갈리지 않도록 'general' 제외 패턴.
  match: matchByCategory(/annual\s*return(?!.*general)|\bAR1?\b/i),
  component: PreSetAnnualReturnPanel,
  meta: { docType: 'ANNUAL_RETURN' },
});

// 3) E-Filing components — PreSetTaskDetailPage / StandaloneEFilingPage 가 task type 별 lookup.
//    EFilingPage.vue 의 dispatch 와 동일한 매핑.
import { registerEFilingComponent } from '../../shared/preset/efilings/efilingRegistry';
import DirectIncorporationEFiling from '../components/direct-incorporation/DirectIncorporationEFiling.vue';
import ChangeAddressEFiling from '../components/change-address/ChangeAddressEFiling.vue';
import ReserveNameEFiling from '../components/reserve-name/ReserveNameEFiling.vue';
import IncorporateReservationEFiling from '../components/incorporate-reservation/IncorporateReservationEFiling.vue';
import OfficerNotificationEFiling from '../components/officer-notification/OfficerNotificationEFiling.vue';
import BeneficialOwnerEFiling from '../components/beneficial-owner/BeneficialOwnerEFiling.vue';

// MY task type code 매핑 (백엔드 L_TASK_CONFIG.TASK_CONFIG_CD 와 일치)
registerEFilingComponent('M32001', ReserveNameEFiling);              // Reserve Name
registerEFilingComponent('M32002', DirectIncorporationEFiling);      // Direct Incorporation
registerEFilingComponent('M32003', OfficerNotificationEFiling);      // Officer Notification
registerEFilingComponent('M32004', IncorporateReservationEFiling);   // Incorporate Reservation
registerEFilingComponent('M32005', BeneficialOwnerEFiling);          // Beneficial Owner
registerEFilingComponent('M32006', ChangeAddressEFiling);            // Change Address
registerEFilingComponent('M32102', ChangeAddressEFiling);            // (alias)

// 4) Address reference data (MY)
import { registerAddressData } from '../../shared/preset/data/addressDataResolver';
import malaysianStates from '../components/shared/data/common/malaysian-states.json';
import cities from '../components/shared/data/common/cities.json';
import postcodes from '../components/shared/data/common/postcodes.json';

registerAddressData('MY', {
  states: malaysianStates as any[],
  cities: cities as any[],
  postcodes: postcodes as any[],
});

// 5) Register Detail field maps (M32105 / M32102 등)
import './register';
